// ============================================================
// WorkOrders.jsx — TAM DOSYA
// Değişiklikler:
// 1. Devam Ediyor → turuncu (sarı yerine)
// 2. Kapatıldı → gri/slate
// 3. İş emri kapatılınca close_failure da çağrılır (banner kapanır)
// ============================================================

import { useState, useEffect } from "react";
import { getWorkOrders, updateWorkOrder } from "../api/client";
import {
  ArrowLeft,
  AlertTriangle,
  Clock,
  CheckCircle,
  User,
  Wrench,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

const API = "https://tez.ulushasturk.com/api.php";

const priorityConfig = {
  critical: {
    label: "Kritik",
    color: "text-red-400",
    bg: "bg-red-500/20 border-red-500/50",
  },
  high: {
    label: "Yüksek",
    color: "text-orange-400",
    bg: "bg-orange-500/20 border-orange-500/50",
  },
  medium: {
    label: "Orta",
    color: "text-yellow-400",
    bg: "bg-yellow-500/20 border-yellow-500/50",
  },
  low: {
    label: "Düşük",
    color: "text-green-400",
    bg: "bg-green-500/20 border-green-500/50",
  },
};

const statusConfig = {
  open: {
    label: "Açık",
    color: "text-red-400",
    bg: "bg-red-500/10 border border-red-500/30",
  },
  in_progress: {
    label: "Devam Ediyor",
    color: "text-orange-400",
    bg: "bg-orange-500/10 border border-orange-500/40",
  },
  closed: {
    label: "Kapatıldı",
    color: "text-slate-400",
    bg: "bg-slate-700/40 border border-slate-600/40",
  },
};

function WorkOrderCard({ order, onUpdate }) {
  const [expanded, setExpanded] = useState(false);
  const [closeNote, setCloseNote] = useState("");
  const [assignedTo, setAssignedTo] = useState(order.assigned_to || "");
  const [loading, setLoading] = useState(false);

  const priority = priorityConfig[order.priority] || priorityConfig.medium;
  const status = statusConfig[order.status] || statusConfig.open;

  const handleStatusChange = async (newStatus) => {
    setLoading(true);
    try {
      // İş emrini güncelle
      await updateWorkOrder({
        id: order.id,
        status: newStatus,
        close_note: closeNote,
        assigned_to: assignedTo,
      });

      // Eğer kapatılıyorsa → close_failure ile test_mode'u da sıfırla
      if (newStatus === "closed") {
        // Hangi makineye ait olduğunu bul
        const machineRes = await fetch(`${API}?action=machines`);
        const machines = await machineRes.json();
        const machine = machines.find(
          (m) => String(m.id) === String(order.machine_id),
        );
        if (machine) {
          await fetch(
            `${API}?action=close_failure&machine_id=${machine.id}&note=${encodeURIComponent(closeNote || "Is emri kapatildi")}`,
          );
        }
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
      onUpdate();
    }
  };

  return (
    <div
      className={`rounded-xl border p-5 mb-3 transition-all ${
        order.status === "closed"
          ? "bg-slate-900/50 border-slate-700/50 opacity-75"
          : priority.bg
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-2">
            <span
              className={`text-xs font-bold px-2 py-0.5 rounded-full border ${priority.bg} ${priority.color}`}
            >
              {priority.label}
            </span>
            <span
              className={`text-xs px-2 py-0.5 rounded-full font-medium ${status.bg} ${status.color}`}
            >
              {status.label}
            </span>
            <span className="text-xs text-slate-400 bg-slate-800 px-2 py-0.5 rounded-full">
              {order.type === "corrective" ? "Düzeltici" : "Önleyici"}
            </span>
          </div>
          <h3 className="font-semibold text-white text-sm mb-1">
            {order.title}
          </h3>
          <p className="text-xs text-slate-400">{order.machine_name}</p>
        </div>
        <button
          onClick={() => setExpanded(!expanded)}
          className="text-slate-400 hover:text-white shrink-0"
        >
          {expanded ? (
            <ChevronUp className="w-4 h-4" />
          ) : (
            <ChevronDown className="w-4 h-4" />
          )}
        </button>
      </div>

      <div className="flex items-center gap-4 mt-3 text-xs text-slate-500">
        <span className="flex items-center gap-1">
          <Clock className="w-3 h-3" />
          {new Date(order.opened_at).toLocaleString("tr-TR")}
        </span>
        <span className="flex items-center gap-1">
          <User className="w-3 h-3" />
          {order.assigned_to || "Atanmadı"}
        </span>
      </div>

      {expanded && (
        <div className="mt-4 border-t border-slate-700 pt-4">
          {order.description && (
            <p className="text-sm text-slate-300 mb-4">{order.description}</p>
          )}

          {order.status !== "closed" && (
            <div className="space-y-3">
              <div>
                <label className="text-xs text-slate-400 block mb-1">
                  Atanan Kişi
                </label>
                <input
                  type="text"
                  value={assignedTo}
                  onChange={(e) => setAssignedTo(e.target.value)}
                  placeholder="Ad Soyad"
                  className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-blue-500"
                />
              </div>
              <div>
                <label className="text-xs text-slate-400 block mb-1">
                  Kapanış Notu
                </label>
                <textarea
                  value={closeNote}
                  onChange={(e) => setCloseNote(e.target.value)}
                  placeholder="Yapılan müdahale, değiştirilen parça vb."
                  rows={3}
                  className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 resize-none"
                />
              </div>
              <div className="flex gap-2">
                {order.status === "open" && (
                  <button
                    onClick={() => handleStatusChange("in_progress")}
                    disabled={loading}
                    className="flex-1 py-2 rounded-lg bg-orange-500/20 border border-orange-500/50 text-orange-400 text-xs font-medium hover:bg-orange-500/30 transition-all"
                  >
                    Çalışmaya Başla
                  </button>
                )}
                <button
                  onClick={() => handleStatusChange("closed")}
                  disabled={loading || !closeNote}
                  className="flex-1 py-2 rounded-lg bg-green-500/20 border border-green-500/50 text-green-400 text-xs font-medium hover:bg-green-500/30 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {loading ? "Kaydediliyor..." : "İş Emrini Kapat"}
                </button>
              </div>
            </div>
          )}

          {order.status === "closed" && (
            <div className="bg-slate-800/50 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="w-4 h-4 text-green-400" />
                <span className="text-xs text-green-400 font-medium">
                  İş Emri Kapatıldı
                </span>
              </div>
              {order.close_note && (
                <>
                  <p className="text-xs text-slate-400 mb-1">Kapanış Notu:</p>
                  <p className="text-sm text-slate-200">{order.close_note}</p>
                </>
              )}
              {order.closed_at && (
                <p className="text-xs text-slate-500 mt-2">
                  {new Date(order.closed_at).toLocaleString("tr-TR")}
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function WorkOrders({ onBack }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("open");

  const fetchOrders = async () => {
    try {
      const res = await getWorkOrders();
      setOrders(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const filtered = orders.filter((o) => {
    if (filter === "open")
      return o.status === "open" || o.status === "in_progress";
    if (filter === "closed") return o.status === "closed";
    return true;
  });

  const openCount = orders.filter(
    (o) => o.status === "open" || o.status === "in_progress",
  ).length;

  return (
    <div className="min-h-screen bg-slate-950">
      <div className="border-b border-slate-800 bg-slate-900/80 backdrop-blur sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={onBack}
              className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h1 className="text-base font-bold text-white flex items-center gap-2">
              <Wrench className="w-4 h-4 text-blue-400" />
              İş Emirleri
            </h1>
          </div>
          {openCount > 0 && (
            <span className="bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full">
              {openCount} açık
            </span>
          )}
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="flex gap-2 mb-6">
          {[
            { key: "open", label: "Açık / Devam Eden" },
            { key: "closed", label: "Kapatılan" },
            { key: "all", label: "Tümü" },
          ].map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-4 py-1.5 rounded-lg text-xs font-medium transition-all ${
                filter === f.key
                  ? "bg-blue-600 text-white"
                  : "bg-slate-800 text-slate-400 hover:bg-slate-700"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20">
            <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-3" />
            <p className="text-slate-400">Bu kategoride iş emri yok</p>
          </div>
        ) : (
          filtered.map((order) => (
            <WorkOrderCard
              key={order.id}
              order={order}
              onUpdate={fetchOrders}
            />
          ))
        )}
      </div>
    </div>
  );
}
